import { useState, type CSSProperties } from "react";
import BoardSlatesAdminScreen from "../BoardSlatesAdminScreen";
import RollAdminScreen from "../RollAdminScreen";

// The media library (ADR 0160, Phase 4): the two global media catalogues the
// super-admin owns — the whiteboard **board slates** (ADR 0058) and the
// pre/post/hold **roll** playlists (ADR 0052/0055) — under one nav item instead
// of two. Each is an existing, tested screen; this only unifies the entry point
// and the chrome (a segmented switch), so nothing about upload/list/delete moves.

type MediaTab = "slates" | "roll";

const TABS: { id: MediaTab; label: string; hint: string }[] = [
  { id: "slates", label: "Board slates", hint: "Whiteboard backdrops" },
  { id: "roll", label: "Pre / Post / Hold roll", hint: "Looping playlists" },
];

export default function MediaLibraryScreen() {
  const [tab, setTab] = useState<MediaTab>("slates");

  const segBtn = (active: boolean): CSSProperties => ({
    appearance: "none",
    border: `1px solid ${active ? "var(--gold-500)" : "var(--border)"}`,
    background: active ? "var(--surface-3)" : "var(--surface-1)",
    color: active ? "var(--text-1)" : "var(--text-3)",
    borderRadius: "var(--radius-control)",
    padding: "8px 16px",
    font: "inherit",
    fontSize: "var(--fs-sm)",
    fontWeight: active ? 600 : 500,
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    lineHeight: 1.2,
  });

  return (
    <div>
      <p style={{ margin: "0 0 16px", fontSize: "var(--fs-md)", color: "var(--text-2)", lineHeight: 1.5, maxWidth: 720 }}>
        The platform-wide media the super-admin owns — the whiteboard board slates operators pick from, and the
        pre / post / hold roll playlists that loop before, after, and during a paused broadcast.
      </p>
      <div role="tablist" aria-label="Media libraries" style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={segBtn(tab === t.id)}
          >
            <span>{t.label}</span>
            <span style={{ fontSize: "var(--fs-3xs)", color: "var(--text-4)", textTransform: "uppercase", letterSpacing: "var(--ls-label)" }}>
              {t.hint}
            </span>
          </button>
        ))}
      </div>
      {tab === "slates" ? <BoardSlatesAdminScreen /> : <RollAdminScreen />}
    </div>
  );
}
